import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {
  ADD_ICON_WITH_HOVER_STYLES,
  ARCHIVED_ANALYSIS_TOOLTIP_TEXT,
} from 'analysis-tool/AnalysisToolConstants';
import {
  useIsAnalysisLayoutCreationEnabled,
  useIsConsolidatedAddSourceEnabled,
} from 'analysis-tool/AnalysisToolFeatureGates';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {
  getAddDataFrameAnalysisViewItem,
  getEnabledDatasetCreationMethods,
  trackSelectedDataFrameCreationMethodEvent,
} from 'analysis-tool/data-frame-creation/DataFrameCreationUtils';
import {DEFAULT_LAYOUT_CONFIG} from 'analysis-tool/data-layout/utils/LayoutCreationUtils';
import {
  isLayoutView,
  isLegacyTopLevelAnalysisView,
  isTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {
  AnalysisMainViewItemType,
  StepInitializationAnalysisMainViewItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import ThirdPartyImportExportModal from 'analysis-tool/ui/main-view/third-party-step/ThirdPartyImportExportModal';
import isArchived from 'archive/isArchived';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FILTER_TABLE_FINISHED_EVENT,
  RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
} from 'performance/processAnalysesEvents';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import {getLatestStepVersion} from 'pipelines/StepConnectionUtils';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import AddIcon from 'ui-library/icons/AddIcon';
import runModal from 'ui-library/runModal';
import type {JSONValue} from 'util/Types';

import AnalysisComponentErrorBoundary from '../components/AnalysisComponentErrorBoundary';
import {DEFAULT_HORIZONTAL_PADDING} from './AnalysisUISidebarConstants';
import {getViewSidebarOverrides} from './getViewSidebarOverrides';
import {
  getOutputSidebarItemDatas,
  getAnalysisViewSidebarItemData,
  getLayoutSidebarItemDatas,
} from './SidebarItemDatas';
import SidebarSection from './SidebarSection';

export interface AnalysisUISidebarProps {
  analysis: AnalysisGraphQL;
  onCloseSidebar: () => void;
  // Analysis steps that require configuration are special because they open intermediate views in the ui. We
  // should not have one onOpen action for each step type
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  selectedItemState: UseSelectedAnalysisItemOutput;
}

/**
 * Wrapper component for handling shared archive-aware ui on sidebar `AddIcon` buttons.
 *
 * When analyses are archived, it renders a disabled `AddIcon` button with standard archived tooltip messaging;
 * otherwise, it renders the given button.
 */
function ArchivedButtonWrapper({
  analysis,
  children,
}: {
  analysis: AnalysisGraphQL;
  children: ReactNode;
}): JSX.Element | null {
  const archivedAddIcon = (
    <Tooltip title={ARCHIVED_ANALYSIS_TOOLTIP_TEXT}>
      <AddIcon disabled={true} />
    </Tooltip>
  );

  const archivedAndUnarchivable = isArchived(analysis) && analysis.permissions.unarchivable;

  return archivedAndUnarchivable ? archivedAddIcon : <>{children}</>;
}

function AnalysisUISidebarContent({
  analysis,
  onCloseSidebar,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  selectedItemState,
}: AnalysisUISidebarProps): JSX.Element {
  const dataFrameCreationMethods = getEnabledDatasetCreationMethods();

  const isConsolidatedAddSourceEnabled = useIsConsolidatedAddSourceEnabled();
  const layoutCreationEnabled = useIsAnalysisLayoutCreationEnabled();
  const viewSidebarOverrides = getViewSidebarOverrides(analysis, {
    excludeLayoutChildViews: layoutCreationEnabled,
  });

  const nonLegacyViews = analysis.views.filter((view) => !isLegacyTopLevelAnalysisView(view));
  const topLevelViews = nonLegacyViews.filter(
    (view) =>
      isTopLevelAnalysisView(view, analysis) &&
      !viewSidebarOverrides.skipViewIds.has(getDefaultClientIdForView(view)) &&
      (!layoutCreationEnabled || !isLayoutView(view))
  );

  const outputSidebarItemDatas = getOutputSidebarItemDatas(analysis);
  const layoutSidebarItemDatas = layoutCreationEnabled
    ? getLayoutSidebarItemDatas(analysis, viewSidebarOverrides)
    : [];
  const canAddDataFrame = dataFrameCreationMethods.length > 0;

  function handleAddDataset(): void {
    onOpenStepInitializationView(getAddDataFrameAnalysisViewItem());
    if (dataFrameCreationMethods.length === 1) {
      trackSelectedDataFrameCreationMethodEvent(
        ActionOrigin.ANALYSIS_TOOL_ANALYSIS_PAGE_DATA_FRAME_SIDEPANEL,
        analysis.id,
        dataFrameCreationMethods.length,
        dataFrameCreationMethods[0]
      );
    }
  }

  async function handleAddLayout(): Promise<void> {
    const response = await AnalysisActions.addAndUpdateSteps(analysis.id, {
      stepsToAdd: [
        {
          stepType: PipelineStepType.LAYOUT,
          stepVersion: getLatestStepVersion(PipelineStepType.LAYOUT),
          config: DEFAULT_LAYOUT_CONFIG as unknown as Record<string, JSONValue>,
          runAfterCreate: true,
        },
      ],
      stepsToUpdate: [],
    });
    const newStepId = response.newStepIds[0];
    if (newStepId != null) {
      selectedItemState.onChangeSelectedItem({
        type: AnalysisMainViewItemType.DATA_LAYOUT,
        stepId: newStepId,
      });
    }
  }

  useLogAnalysisProfilerTimingFinished(isAnalysisRunning(analysis), [
    RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    FILTER_TABLE_FINISHED_EVENT,
  ]);

  return (
    <div className={css(styles.sidebarContentWrapper)}>
      <div className={css(styles.sectionsWrapper)}>
        <SidebarSection
          analysis={analysis}
          headerButton={
            isConsolidatedAddSourceEnabled ? null : (
              <ArchivedButtonWrapper analysis={analysis}>
                {analysis.permissions.writable ? (
                  <Tooltip disabled={!canAddDataFrame} title="Add data">
                    <AddIcon
                      altText="Add data"
                      className={css(styles.addIcon)}
                      disabled={!canAddDataFrame}
                      onClick={handleAddDataset}
                    />
                  </Tooltip>
                ) : null}
              </ArchivedButtonWrapper>
            )
          }
          onToggleSidebar={onCloseSidebar}
          sidebarItemDatas={topLevelViews.map((view) =>
            getAnalysisViewSidebarItemData(view, analysis, viewSidebarOverrides)
          )}
          selectedItemState={selectedItemState}
          onOpenStepInitializationView={onOpenStepInitializationView}
          title="Analysis data"
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
        />
        {layoutCreationEnabled && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              headerButton={
                <ArchivedButtonWrapper analysis={analysis}>
                  {analysis.permissions.writable ? (
                    <Tooltip title="Add layout">
                      <AddIcon
                        altText="Add layout"
                        className={css(styles.addIcon)}
                        onClick={handleAddLayout}
                      />
                    </Tooltip>
                  ) : null}
                </ArchivedButtonWrapper>
              }
              sidebarItemDatas={layoutSidebarItemDatas}
              selectedItemState={selectedItemState}
              onOpenStepInitializationView={onOpenStepInitializationView}
              title="Layouts"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
        {outputSidebarItemDatas.length > 0 && (
          <div className={css(styles.secondarySidebarSectionWrapper)}>
            <SidebarSection
              analysis={analysis}
              defaultExpandedTier={0}
              onOpenStepInitializationView={onOpenStepInitializationView}
              sidebarItemDatas={outputSidebarItemDatas}
              selectedItemState={selectedItemState}
              title="Outputs"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
            />
          </div>
        )}
      </div>
      {!isConsolidatedAddSourceEnabled && (
        <div className={css(styles.thirdPartyImportExportButtonWrapper)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            fullWidth={true}
            onClick={() => {
              runModal(ThirdPartyImportExportModal, {
                onRequestSubmit: () => {},
                onRequestClose: () => {},
                analysis,
              });
            }}
            size={ButtonSize.MEDIUM}
            text="Connect with external tool"
            dataTestAttrs={{component: 'AnalysisUISidebar', element: 'thirdPartyImportButton'}}
          />
        </div>
      )}
    </div>
  );
}

export default function AnalysisUISidebar(props: AnalysisUISidebarProps): JSX.Element {
  return (
    <AnalysisComponentErrorBoundary
      errorExtras={{analysisId: props.analysis.id, selectedItemState: props.selectedItemState}}
    >
      <AnalysisUISidebarContent {...props} />
    </AnalysisComponentErrorBoundary>
  );
}

const styles = StyleSheet.create({
  sidebarContentWrapper: {
    paddingRight: DEFAULT_HORIZONTAL_PADDING,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionsWrapper: {
    flexGrow: 1,
    overflowX: 'auto',
  },
  secondarySidebarSectionWrapper: {
    marginTop: 16,
  },
  thirdPartyImportExportButtonWrapper: {
    marginTop: 8,
  },
  addIcon: ADD_ICON_WITH_HOVER_STYLES,
});
